import { useCallback, useEffect, useRef, useState } from 'react';
import type { IWorkAreaNavigationPreferences } from '@featit/shared';
import { useStores } from '@/hooks/useStores';
import { localSyncStore } from '@/local-sync/localSyncStore';
import { userPreferencesApi } from '@/services/UserPreferencesApi';
import {
  actionFailureTextIn,
  failureToSay,
  type HeldSentenceType,
  sayFailure,
} from '@/services/actionFailure';
import { useT } from '@/i18n/LocaleProvider';

const CACHE_COLLECTION = 'user-work-area-navigation';
const REFRESH_INTERVAL_MS = 30_000;
type PreferenceListener = (userId: string, preferences: IWorkAreaNavigationPreferences) => void;
const preferenceListeners = new Set<PreferenceListener>();

function publishPreferences(userId: string, preferences: IWorkAreaNavigationPreferences): void {
  preferenceListeners.forEach((listener) => listener(userId, preferences));
}

/** Every mounted copy's `refresh`, so one call reads the order again everywhere. */
const refreshers = new Set<() => Promise<void>>();

/**
 * F-0530 (AC-19.4): read the work-area order again in every mounted copy now — the app's edit view
 * closed in another web view, so the order may have changed without this one's 30-second refresh
 * having seen it.
 */
export function refreshWorkAreaNavigationPreferences(): Promise<void> {
  return Promise.all([...refreshers].map((refresh) => refresh())).then(() => undefined);
}

export function useWorkAreaNavigationPreferences() {
  const t = useT();
  const { authStore } = useStores();
  const userId = authStore.user?.id ?? null;
  const sequenceRef = useRef(0);
  const networkAppliedRef = useRef(false);
  const [preferences, setLocalPreferences] = useState<IWorkAreaNavigationPreferences | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<HeldSentenceType | null>(null);

  const setPreferences = useCallback(
    (next: IWorkAreaNavigationPreferences) => {
      setLocalPreferences(next);
      if (userId) publishPreferences(userId, next);
    },
    [userId],
  );

  const refresh = useCallback(async () => {
    if (!userId) return;
    const sequence = ++sequenceRef.current;
    try {
      const next = await userPreferencesApi.workAreaNavigation();
      if (sequence !== sequenceRef.current) return;
      networkAppliedRef.current = true;
      setPreferences(next);
      setError(null);
      void localSyncStore.write(CACHE_COLLECTION, userId, next, {
        revision: next.revision,
      });
    } catch (cause) {
      if (sequence !== sequenceRef.current) return;
      setError(failureToSay(cause, 'navigation.actions.syncWorkAreaOrder'));
    }
  }, [setPreferences, userId]);

  useEffect(() => {
    sequenceRef.current += 1;
    networkAppliedRef.current = false;
    setLocalPreferences(null);
    setError(null);
    if (!userId) return;
    let active = true;
    void localSyncStore
      .read<IWorkAreaNavigationPreferences>(CACHE_COLLECTION, userId)
      .then((cached) => {
        if (!active || !cached || networkAppliedRef.current) return;
        setLocalPreferences(cached);
      })
      .finally(() => {
        if (active) void refresh();
      });
    const timer = window.setInterval(() => void refresh(), REFRESH_INTERVAL_MS);
    refreshers.add(refresh);
    return () => {
      active = false;
      window.clearInterval(timer);
      refreshers.delete(refresh);
    };
  }, [refresh, userId]);

  useEffect(() => {
    if (!userId) return;
    const listener: PreferenceListener = (updatedUserId, next) => {
      if (updatedUserId === userId) setLocalPreferences(next);
    };
    preferenceListeners.add(listener);
    return () => {
      preferenceListeners.delete(listener);
    };
  }, [userId]);

  const save = useCallback(
    async (next: IWorkAreaNavigationPreferences) => {
      if (!userId) throw new Error('Sign in to save your work-area order.');
      setSaving(true);
      setError(null);
      try {
        const saved = await userPreferencesApi.updateWorkAreaNavigation(next);
        setPreferences(saved);
        await localSyncStore.write(CACHE_COLLECTION, userId, saved, {
          revision: saved.revision,
        });
        return saved;
      } catch (cause) {
        const message =
          httpStatus(cause) === 409
            ? 'Your work-area order changed in another window. Reload it, then reorder again.'
            : actionFailureTextIn(t, cause, t('navigation.actions.saveWorkAreaOrder'));
        setError(message);
        throw cause;
      } finally {
        setSaving(false);
      }
    },
    [setPreferences, userId, t],
  );

  return {
    preferences,
    setPreferences,
    saving,
    error: sayFailure(t, error),
    refresh,
    save,
  };
}

function httpStatus(cause: unknown): number | undefined {
  return (cause as { response?: { status?: number } })?.response?.status;
}
